import React, { useState, useRef, useEffect } from 'react';
import './Contact.css';
import ScrollSection from '../../components/ScrollSection';
import { useContent } from '../../context/ContentContext';
import { useInView } from 'react-intersection-observer';

const COMMANDS = {
  help: () => [
    { text: 'Available commands:', type: 'info' },
    { text: '  contact -    show contact links', type: 'output' },
    { text: '  hire -       show hiring status', type: 'output' },
    { text: '  github -     open GitHub profile', type: 'output' },
    { text: '  linkedin -   open LinkedIn profile', type: 'output' },
    //{ text: '  resume:     download resume', type: 'output' },
    { text: '  clear -     clear terminal', type: 'output' },
  ],
  contact: (content) => [
    { text: 'Contact information:', type: 'info' },
    { text: '  LinkedIn  → ' + content.hero.linkedin, type: 'link', url: content.hero.linkedin },
    { text: '  GitHub    → ' + content.hero.github, type: 'link', url: content.hero.github },
  ],
  hire: (content) => [
    { text: 'Hiring status:', type: 'info' },
    { text: '  Status    → ' + content.hero.availability, type: 'success' },
    { text: '  Role      → SRE / Infrastructure Engineer', type: 'output' },
    { text: '  Type      → Full-time, Remote preferred', type: 'output' },
    { text: '  Location  → ' + (content.hero.location || 'San Jose, CA'), type: 'output' },
  ],
  github: (content) => [
    { text: 'Opening GitHub...', type: 'info', action: () => window.open(content.hero.github, '_blank', 'noopener,noreferrer') },
  ],
  linkedin: (content) => [
    { text: 'Opening LinkedIn...', type: 'info', action: () => window.open(content.hero.linkedin, '_blank', 'noopener,noreferrer') },
  ],
  resume: () => [
    { text: 'Downloading resume...', type: 'info', action: () => {
      const a = document.createElement('a');
      a.href = process.env.PUBLIC_URL + '/resume1.pdf';
      a.download = 'JoseAyala_Resume.pdf';
      a.click();
    }},
  ],
};

const WELCOME = [
  { text: 'Type help to see available commands.', type: 'output' },
];

function Contact() {
  const { content, loading } = useContent();
  const [history, setHistory] = useState(WELCOME);
  const [input, setInput] = useState('');
  const [cmdHistory, setCmdHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef(null);
  const { ref: sectionRef, inView } = useInView({ threshold: 0.3, triggerOnce: false });

  useEffect(() => {
    if (inView && inputRef.current) {
      inputRef.current.focus();
    }
  }, [inView]);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [history]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const cmd = input.trim().toLowerCase();
    if (!cmd) return;

    const newHistory = [...history, { text: '$ ' + cmd, type: 'prompt' }];

    if (cmd === 'clear') {
      setHistory(WELCOME);
      setInput('');
      setCmdHistory(prev => [cmd, ...prev]);
      setHistoryIndex(-1);
      return;
    }

    if (COMMANDS[cmd]) {
      const results = COMMANDS[cmd](content);
      results.forEach(r => { if (r.action) r.action(); });
      setHistory([...newHistory, ...results]);
    } else {
      setHistory([...newHistory, { text: 'command not found: ' + cmd + '. Type help for available commands.', type: 'error' }]);
    }

    setCmdHistory(prev => [cmd, ...prev]);
    setHistoryIndex(-1);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const newIndex = Math.min(historyIndex + 1, cmdHistory.length - 1);
      setHistoryIndex(newIndex);
      setInput(cmdHistory[newIndex] || '');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const newIndex = Math.max(historyIndex - 1, -1);
      setHistoryIndex(newIndex);
      setInput(newIndex === -1 ? '' : cmdHistory[newIndex] || '');
    }
  };

  if (loading || !content) return null;

  return (
    <ScrollSection name="contact">
      <div className="section-container">
        <h2 className="section-title" data-aos="fade-up" data-aos-duration="800">Contact</h2>
        <div className="terminal-wrapper" data-aos="fade-up" data-aos-duration="800" data-aos-delay="200">
          <div className="terminal-titlebar">
            <div className="terminal-dots">
              <span className="terminal-dot red"></span>
              <span className="terminal-dot yellow"></span>
              <span className="terminal-dot green"></span>
            </div>
          </div>
          <div className="terminal-body" onClick={() => inputRef.current && inputRef.current.focus()}>
            {history.map((line, i) => (
              <div key={i} className={"terminal-line terminal-line--" + line.type}>
                {line.type === 'link'
                  ? <span>
                      {line.text.split('→ ')[0]}{'→ '}
                      <a href={line.url} target="_blank" rel="noreferrer" className="terminal-link">
                        {line.url}
                      </a>
                    </span>
                  : line.text
                }
              </div>
            ))}
            <form onSubmit={handleSubmit} className="terminal-input-row">
              <span className="terminal-prompt">$</span>
              <input
                ref={inputRef}
                className="terminal-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                aria-label="Terminal input"
              />
            </form>
            <div ref={bottomRef} />
          </div>
        </div>
      </div>
    </ScrollSection>
  );
}

export default Contact;
